import { scrollbarCompensate } from 'scrollbar-compensate';
import {
  listenOnce,
  createFragment,
  isFunction,
  preloadImage,
  load,
} from 'utils';

/**
 * Lifecycle methods (data-methods):
 * --------
 * preload    - before loading content (skips onload)
 * onload     - ajax content loaded
 * onrender   - the content is rendered in the modal
 * onopen     - the modal finishes animating in
 * onclose    - the modal finishes animating out
 * ondestroy  - the contents are cleared in the modal
 * --------
 * only one modal can be open at a time
 */

class Modal {
  constructor() {
    this.methods = {};
    document.addEventListener('click', this.onclick);
  }

  /**
   * @param {String} namespace
   * @param {Function} method
   */
  addMethod(namespace, lifecycle, method) {
    if (!isFunction(method)) {
      console.error(`${namespace}.${method.name} is not a function`);
      console.error(method);
      return;
    }

    if (!(namespace in this.methods)) {
      this.methods[namespace] = {};
    }

    if (lifecycle in this.methods[namespace]) {
      console.error(`${namespace}.${method.name} is already defined`);
      return;
    }

    if (!Array.isArray(this.methods[namespace][lifecycle])) {
      this.methods[namespace][lifecycle] = [];
    }

    this.methods[namespace][lifecycle].push(method);
  }

  /**
   * @param {String} lifecycle
   */
  executeMethods(lifecycle, ...args) {
    if (!this.currentMethods) {
      return;
    }

    const methods = this.currentMethods[lifecycle];

    if (!methods || !methods.length) {
      return;
    }

    methods.forEach(method => {
      if (isFunction(method)) {
        method(...args);
      }
    });
  }

  onkeydown = event => {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  };

  onclick = event => {
    this.data = event.target.dataset;
    // Early exit
    if (!this.data.modal) {
      return;
    }
    event.preventDefault();
    // Close if 'close'
    if (this.data.modal === 'close') {
      this.closeModal();
      return;
    }
    const targetModal = document.getElementById(this.data.modal + '-modal');

    // Doesn't have a target
    if (!targetModal) {
      console.error(`${this.data.modal}-modal isn't available`);
      return;
    }
    const url = this.data.url || event.target.href;
    this.currentMethods = this.methods[this.data.methods];
    this.openModal(targetModal, url);
  };

  async appendContent(url) {
    let fragment;
    let response;
    if (this.data.type === 'img') {
      try {
        fragment = await preloadImage(url);
      } catch (error) {
        console.error(error);
      }
    } else {
      response = await load(url);
      this.executeMethods('onload');
      fragment = createFragment(response);
    }
    if (!this.contentArea) {
      throw Error('no content area in modal window');
    }
    this.contentArea.innerHTML = '';
    this.contentArea.appendChild(fragment);
    return response;
  }

  async openModal(target, url) {
    this.currentModal = target;
    this.contentArea = this.currentModal.querySelector('.modal-content');
    this.hasExistingContent = this.contentArea.hasChildNodes();

    if (url && !this.currentMethods.preload) {
      try {
        await this.appendContent(url);
      } catch (error) {
        console.error(error);
        this.currentModal = null;
        this.contentArea = null;
        return;
      }
    }

    if (url && this.currentMethods.preload) {
      this.executeMethods('preload', url);
    }

    listenOnce(this.currentModal, 'transitionend').then(() => {
      this.executeMethods('onopen');
    });

    document.addEventListener('keydown', this.onkeydown);
    this.currentModal.classList.add('is-open');
    this.currentModal.setAttribute('aria-hidden', false);
    scrollbarCompensate.compensate();
    document.documentElement.classList.add('noscroll');

    this.executeMethods('onrender');
  }

  closeModal() {
    listenOnce(this.currentModal, 'transitionend').then(() => {
      this.executeMethods('onclose');
      this.currentModal = null;
      if (!this.hasExistingContent) {
        this.contentArea.innerHTML = '';
      }
    });

    document.removeEventListener('keydown', this.onkeydown);
    scrollbarCompensate.reset();
    this.currentModal.classList.remove('is-open');
    this.currentModal.setAttribute('aria-hidden', true);
    document.documentElement.classList.remove('noscroll');
    this.contentArea.scrollTop = 0;

    this.executeMethods('ondestroy');
  }
}

export const modal = new Modal();



// WEBPACK FOOTER //
// ./src/js/modal.js